import { Check } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import { body14Styles, smallcapsBold13Styles } from "@constants/typography"
import _ from "lodash-es"
import React, { useState } from "react"
import styled from "styled-components"

export const Container = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition: opacity ${animation.medium} ${cubicBezier};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  pointer-events: ${(props) => (props.$isOpen ? "initial" : "none")};
  width: 100%;
  max-height: 352px;
  overflow: hidden;
  overflow-y: auto;
  background: ${colors.offWhite};
  border: 1px solid ${colors.dividerLineGray};
  box-shadow: 0px 0px 20px ${colors.black04};
  border-radius: 4px;
`

const Brands = styled.div`
  align-items: center;
  justify-content: flex-start;
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0 12px;
  border-bottom: 1px solid ${colors.dividerLineGray};
`

const Brand = styled.div<{ $active?: boolean }>`
  ${smallcapsBold13Styles}
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;
  width: 96px;
  height: 32px;
  padding-top: 2px;
  border: 1px solid ${colors.black};
  border-radius: 2px;
  background-color: ${(props) =>
    props.$active ? colors.black : colors.offWhite};
  color: ${(props) => (props.$active ? colors.offWhite : colors.black)};
`

const Devices = styled.div`
  width: 100%;
  padding: 8px 12px;
  overflow: hidden;
  overflow-y: auto;
`

const Device = styled.div`
  ${body14Styles}
  cursor: pointer;
  position: relative;
  margin-bottom: 4px;
  padding: 12px 0 12px 24px;
  font-weight: 400;
  color: ${colors.black};

  &:hover {
    background-color: ${colors.dividerLineGray};
  }
`

const IconWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 0;
  flex-direction: row;
  align-items: center;
  display: flex;
  width: 24px;
  height: 24px;
`

interface DeviceDropdownProps {
  currentDevice: string
  devices: Array<{
    brand: string
    device: string
    onClick: () => void
  }>
  isOpen?: boolean
  onRequestClose?: () => void
}

const DeviceDropdown: React.FC<DeviceDropdownProps> = ({
  currentDevice,
  devices,
  isOpen,
  onRequestClose,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [brands, setBrands] = useState([])
  const [brand, setBrand] = useState("")

  React.useEffect(() => {
    const uniqueBrands: any = _.uniq(
      _.sortBy(devices.map((d) => d.brand)),
      (b) => b
    )
    setBrands(uniqueBrands)
  }, [devices])

  React.useEffect(() => {
    if (currentDevice) {
      const currentBrand = devices.find(
        (d) => d.device === currentDevice
      )?.brand
      setBrand(currentBrand || "")
    }
  }, [currentDevice])

  React.useEffect(() => {
    if (brands.length === 1) {
      setBrand(brands[0])
    }
  }, [brands])

  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        onRequestClose
      ) {
        onRequestClose()
      }
    }

    document.addEventListener("click", handleClick)
    return () => {
      document.removeEventListener("click", handleClick)
    }
  }, [])

  return (
    <Container $isOpen={!!isOpen} ref={containerRef}>
      <Brands>
        {brands.map((b) => (
          <Brand
            $active={brand === b}
            key={b}
            onClick={(e) => {
              e.stopPropagation()
              setBrand(b)
            }}
          >
            {b}
          </Brand>
        ))}
      </Brands>
      <Devices>
        {devices.map((d, i) => (
          <Device key={d.device} onClick={d.onClick} tabIndex={i}>
            {d.device === currentDevice && (
              <IconWrapper>
                <Check />
              </IconWrapper>
            )}
            <span>{d.device}</span>
          </Device>
        ))}
      </Devices>
    </Container>
  )
}

export default DeviceDropdown
